/**
 * DriverFuelPanel — « Carburant » sur l'accueil conducteur web (2026-09-14).
 * =============================================================================
 * Repris de l'onglet Carburant de l'APK, élagué (JGS : « aussi simple que
 * commander un Uber ») :
 *   - le dernier plein, en une ligne ;
 *   - « Saisir un plein » : un formulaire court, prérempli par le serveur
 *     (`/drivers/me/fuel/prefill` — la GARDE DE POOL en cours d'abord) ;
 *   - le gazole le moins cher autour de moi (position du navigateur, sinon
 *     l'U.T. du conducteur) ;
 *   - ma carte carburant (`DriverFuelCard`, déjà là).
 *
 * Ce qu'on NE recopie PAS de l'APK (irritants JGS vérifiés le 13/09) : saisie
 * toute au clavier, bouton grisé muet, « plein complet » coché d'office, date
 * non modifiable, trois emplacements photo qui paraissent obligatoires.
 *
 * ⚠️ Kilométrage : l'estimé télématique s'affiche en REPÈRE, il n'est jamais
 * prérempli — préremplir avait recalé un boîtier de 52 km (JOURNAL_BETA 19/08).
 * Le kilométrage lu au compteur est obligatoire.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Fuel, MapPin, Plus, X, RefreshCw } from 'lucide-react';
import { driverSelfAPI, fuelPricesAPI } from '../../../api/drivers.js';
import { useFormat } from '../../../i18n/format.js';
import DriverFuelCard from './DriverFuelCard.jsx';

/** `vehicles.fuel_type` (énum FMM) → carburant de prix-carburants.gouv.fr. `null` = pas de prix à chercher. */
export function priceFuelTypeOf(fuelType) {
  const v = String(fuelType || '').toLowerCase();
  if (!v || v === 'diesel' || v === 'gazole') return 'gazole';
  if (v === 'petrol' || v === 'gasoline' || v === 'essence' || v === 'sp95') return 'sp95';
  if (v === 'e10' || v === 'sp95_e10') return 'e10';
  if (v === 'sp98') return 'sp98';
  if (v === 'e85' || v === 'ethanol') return 'e85';
  if (v === 'lpg' || v === 'gpl' || v === 'gplc') return 'gplc';
  return null; // électrique, hydrogène : rien à afficher
}

const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function FuelForm({ prefill, onDone, onCancel }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ fuel_date: todayInput(), odometer: '', liters: '', total_cost: '', is_full_tank: false, receipt: null });
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const create = useMutation({
    mutationFn: (fd) => driverSelfAPI.createFuelLog(fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['driver-fuel'] });
      queryClient.invalidateQueries({ queryKey: ['driver-activity'] });
      onDone?.();
    },
    onError: (err) => setError(err?.message || t('poolDriver.fuelPanel.errorGeneric')),
  });

  // Ce qui manque, DIT — jamais un bouton grisé muet.
  const missing = [];
  if (!form.fuel_date) missing.push(t('poolDriver.fuelPanel.date'));
  if (!(parseFloat(form.liters) > 0)) missing.push(t('poolDriver.fuelPanel.liters'));
  if (!(parseFloat(form.total_cost) > 0)) missing.push(t('poolDriver.fuelPanel.totalCost'));
  if (!(parseInt(form.odometer, 10) > 0)) missing.push(t('poolDriver.fuelPanel.odometer'));
  const belowLast = prefill?.last_odometer != null && parseInt(form.odometer, 10) > 0 && parseInt(form.odometer, 10) < prefill.last_odometer;

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    const fd = new FormData();
    fd.append('fuel_date', form.fuel_date);
    fd.append('liters', String(parseFloat(form.liters)));
    fd.append('total_cost', String(parseFloat(form.total_cost)));
    fd.append('odometer', String(parseInt(form.odometer, 10)));
    fd.append('is_full_tank', form.is_full_tank ? 'true' : 'false');
    if (form.receipt) fd.append('photo_receipt', form.receipt);
    create.mutate(fd);
  };

  const field = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500';

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="driver-fuel-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">
          {t('poolDriver.fuelPanel.formTitle', { vehicle: prefill?.vehicle_plate || '—' })}
        </p>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800" aria-label={t('poolDriver.fuelPanel.cancel')}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-gray-700">
          {t('poolDriver.fuelPanel.date')}
          <input type="date" value={form.fuel_date} onChange={set('fuel_date')} max={todayInput()} className={field} required />
        </label>
        <label className="text-sm text-gray-700">
          {t('poolDriver.fuelPanel.odometer')}
          <input type="number" inputMode="numeric" min="0" step="1" value={form.odometer} onChange={set('odometer')} className={field} required
            placeholder={prefill?.last_odometer != null ? String(prefill.last_odometer) : ''} />
          {prefill?.estimated_odometer != null && (
            <span className="mt-0.5 block text-xs text-gray-500">
              {t('poolDriver.fuelPanel.odometerHint', { km: fmt.number(prefill.estimated_odometer) })}
            </span>
          )}
          {belowLast && (
            <span className="mt-0.5 block text-xs text-red-600">
              {t('poolDriver.fuelPanel.odometerBelowLast', { km: fmt.number(prefill.last_odometer) })}
            </span>
          )}
        </label>
        <label className="text-sm text-gray-700">
          {t('poolDriver.fuelPanel.liters')}
          <input type="number" inputMode="decimal" min="0" step="0.01" value={form.liters} onChange={set('liters')} className={field} required />
        </label>
        <label className="text-sm text-gray-700">
          {t('poolDriver.fuelPanel.totalCost')}
          <input type="number" inputMode="decimal" min="0" step="0.01" value={form.total_cost} onChange={set('total_cost')} className={field} required />
        </label>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={form.is_full_tank} onChange={set('is_full_tank')} className="h-4 w-4 rounded border-gray-300 text-emerald-600" />
        {t('poolDriver.fuelPanel.fullTank')}
      </label>

      <label className="block text-sm text-gray-700">
        {t('poolDriver.fuelPanel.receipt')}
        <input type="file" accept="image/*" capture="environment" onChange={(e) => setForm((f) => ({ ...f, receipt: e.target.files?.[0] || null }))} className="mt-1 block w-full text-sm text-gray-600" />
      </label>

      {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <button
        type="submit"
        disabled={missing.length > 0 || belowLast || create.isPending}
        className="flex min-h-[48px] w-full items-center justify-center rounded-lg bg-emerald-600 px-4 py-2.5 text-base font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
      >
        {create.isPending ? t('poolDriver.fuelPanel.saving') : t('poolDriver.fuelPanel.save')}
      </button>
      {missing.length > 0 && !create.isPending && (
        <p className="text-center text-xs text-gray-500">{t('poolDriver.fuelPanel.missing', { fields: missing.join(', ') })}</p>
      )}
    </form>
  );
}

/** Le gazole le moins cher autour d'un point — une station, la moins chère du rayon. */
function CheapestStation({ coords, fuelType }) {
  const { t } = useTranslation();
  const priceType = priceFuelTypeOf(fuelType);
  const { data, isLoading } = useQuery({
    queryKey: ['fuel-prices', 'nearby', coords?.lat, coords?.lon, priceType],
    queryFn: () => fuelPricesAPI.nearby({ lat: coords.lat, lon: coords.lon, radius: 10, fuelType: priceType }),
    enabled: !!coords && !!priceType,
    staleTime: 30 * 60_000,
    retry: false,
  });
  if (!coords || !priceType) return null;
  const stations = (data?.ok ? data.data : []) || [];
  const cheapest = [...stations].filter((s) => s.price != null).sort((a, b) => a.price - b.price)[0] || null;
  if (isLoading) return <div className="h-10 animate-pulse rounded-lg bg-gray-100" />;
  if (!cheapest) return null;
  const avg = stations.length ? stations.reduce((a, s) => a + (s.price || 0), 0) / stations.length : null;
  const maps = `https://www.google.com/maps/dir/?api=1&destination=${cheapest.lat},${cheapest.lon}`;
  return (
    <a href={maps} target="_blank" rel="noreferrer" className="block rounded-lg bg-emerald-50 px-3 py-2 hover:bg-emerald-100" data-testid="cheapest-station">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-emerald-800">{t('poolDriver.fuelPanel.cheapest', { fuel: priceType })}</p>
        <p className="text-lg font-bold text-emerald-900">{cheapest.price.toFixed(3)} €/L</p>
      </div>
      <p className="truncate text-sm text-emerald-900">
        <MapPin className="mr-1 inline h-3.5 w-3.5" />
        {cheapest.name || cheapest.brand || cheapest.address || '—'} · {cheapest.city} · {cheapest.distance_km} km
      </p>
      {avg != null && stations.length > 1 && (
        <p className="text-xs text-emerald-800/80">{t('poolDriver.fuelPanel.average', { price: avg.toFixed(3), count: stations.length })}</p>
      )}
    </a>
  );
}

/**
 * @param {Object} props
 * @param {Object|null} props.vehicle - le véhicule courant (garde ou réservation), pour le type de carburant
 * @param {Array} props.fuelCards - mes cartes (déjà lues par l'accueil)
 * @param {{lat:number, lon:number}|null} props.fallbackCoords - l'U.T. du conducteur, si le navigateur ne donne pas la position
 */
export function DriverFuelPanel({ situation = null, vehicle = null, fuelCards = [], fallbackCoords = null }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);

  // Position du navigateur, sans insister : refus ou absence = l'U.T. fait foi.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => {},
        { maximumAge: 10 * 60_000, timeout: 5000 }
      );
    } catch { /* navigateur sans API : l'U.T. fait foi */ }
  }, []);
  const where = coords || fallbackCoords;

  const logs = useQuery({
    queryKey: ['driver-fuel', 'logs'],
    queryFn: () => driverSelfAPI.listFuelLogs(),
    staleTime: 60_000,
    retry: false,
  });
  const prefill = useQuery({
    queryKey: ['driver-fuel', 'prefill'],
    queryFn: () => driverSelfAPI.getFuelPrefill(),
    enabled: open,
    retry: false,
  });

  const last = useMemo(() => {
    const rows = logs.data?.ok ? logs.data.data : [];
    return [...(rows || [])].sort((a, b) => new Date(b.fuel_date) - new Date(a.fuel_date))[0] || null;
  }, [logs.data]);

  // Le serveur répond en anglais et en 400 quand il n'y a pas de véhicule en main :
  // l'écran le dit en français, sans recopier le message technique — et il dit
  // POURQUOI selon la situation : créneau manqué (réserver à nouveau), réservation
  // pas encore prise (prendre d'abord), rien du tout (contacter le gestionnaire).
  const NO_VEHICLE_KEY = {
    missed: 'poolDriver.fuelPanel.noVehicleMissed',
    waiting: 'poolDriver.fuelPanel.noVehicle',
    requested: 'poolDriver.fuelPanel.noVehicleRequested',
  };
  const prefillError = prefill.isError
    ? (prefill.error?.status === 400 || /no vehicle/i.test(prefill.error?.message || '')
      ? t(NO_VEHICLE_KEY[situation] || 'poolDriver.fuelPanel.noVehicleNone')
      : prefill.error?.message || t('poolDriver.fuelPanel.errorGeneric'))
    : null;

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4" data-testid="driver-fuel-panel">
      <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
        <Fuel className="h-4 w-4 text-emerald-600" />
        {t('poolDriver.fuelPanel.title')}
      </h2>

      {last ? (
        <p className="text-sm text-gray-700">
          {t('poolDriver.fuelPanel.lastFill', {
            liters: last.liters != null ? fmt.number(Number(last.liters)) : '—',
            cost: last.total_cost != null ? fmt.number(Number(last.total_cost)) : '—',
            date: last.fuel_date ? fmt.date(last.fuel_date) : '—',
          })}
        </p>
      ) : (
        !logs.isLoading && <p className="text-sm text-gray-500">{t('poolDriver.fuelPanel.noFill')}</p>
      )}

      {open ? (
        prefill.data?.ok ? (
          <FuelForm prefill={prefill.data.data} onDone={() => setOpen(false)} onCancel={() => setOpen(false)} />
        ) : prefillError ? (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            <p>{prefillError}</p>
            <button type="button" onClick={() => prefill.refetch()} className="mt-1 inline-flex items-center gap-1 text-xs font-medium underline">
              <RefreshCw className="h-3 w-3" /> {t('poolDriver.fuelPanel.retry')}
            </button>
          </div>
        ) : (
          <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
        )
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-base font-semibold text-white hover:bg-emerald-700"
        >
          <Plus className="h-5 w-5" />
          {t('poolDriver.fuelPanel.add')}
        </button>
      )}

      <CheapestStation coords={where} fuelType={vehicle?.fuel_type} />

      <DriverFuelCard cards={fuelCards} t={t} fmt={fmt} />
    </section>
  );
}

export default DriverFuelPanel;
